import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchBlogs, createBlog, deleteBlog, type Blog } from '@/admin/lib/api';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/admin/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/admin/components/ui/dialog';
import { Input } from '@/admin/components/ui/input';
import { Label } from '@/admin/components/ui/label';
import { Skeleton } from '@/admin/components/ui/skeleton';
import {
  IconPlus,
  IconTrash,
  IconChevronRight,
  IconArticle,
  IconRefresh,
} from '@tabler/icons-react';

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'published':
      return (
        <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-0 font-medium">
          Published
        </Badge>
      );
    case 'archived':
      return (
        <Badge variant="secondary" className="font-medium">
          Archived
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-muted-foreground font-medium">
          Draft
        </Badge>
      );
  }
}

export const BlogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingBlog, setDeletingBlog] = useState<Blog | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Form states
  const [newTitle, setNewTitle] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchBlogs(50, 0);
      setBlogs(data.blogs || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Generate slug from title (lowercase, hyphenated)
  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleCreateBlog = async () => {
    if (!newTitle.trim()) return;

    const slug = generateSlug(newTitle);
    if (!slug) {
      setCreateError('Title must contain at least one letter or number');
      return;
    }

    setCreating(true);
    setCreateError(null);
    try {
      const created = await createBlog({
        title: newTitle.trim(),
        slug,
        status: 'draft',
      });
      setBlogs(prev => [created, ...prev]);
      setTotal(prev => prev + 1);
      setCreateDialogOpen(false);
      setNewTitle('');
      // Navigate to detail page
      navigate(`/blogs/${created.id}`);
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : 'Failed to create blog'
      );
    } finally {
      setCreating(false);
    }
  };

  const handleOpenCreateDialog = (open: boolean) => {
    setCreateDialogOpen(open);
    if (open) {
      setCreateError(null);
      setNewTitle('');
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, b: Blog) => {
    e.stopPropagation();
    setDeletingBlog(b);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingBlog) return;
    setDeleting(true);
    try {
      await deleteBlog(deletingBlog.id);
      setBlogs(prev => prev.filter(b => b.id !== deletingBlog.id));
      setTotal(prev => prev - 1);
      setDeleteDialogOpen(false);
      setDeletingBlog(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete blog');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-card">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <IconArticle className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Blogs</h1>
            <p className="text-xs text-muted-foreground">{total} total</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={loadData}
            disabled={loading}
          >
            <IconRefresh
              className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
            />
          </Button>
          <Button onClick={() => handleOpenCreateDialog(true)} size="sm">
            <IconPlus className="h-4 w-4 mr-1.5" />
            New Blog
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="mx-6 mt-4 p-3 bg-destructive/10 text-destructive text-sm rounded-lg border border-destructive/20">
            {error}
          </div>
        )}

        <div className="p-6">
          <div className="border rounded-lg bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="font-semibold w-[40%]">Title</TableHead>
                  <TableHead className="font-semibold">Author</TableHead>
                  <TableHead className="font-semibold w-28">Status</TableHead>
                  <TableHead className="font-semibold w-32">
                    Published
                  </TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i} className="h-16">
                      <TableCell>
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-64" />
                          <Skeleton className="h-3 w-32" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-8 w-16 ml-auto" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : blogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-48">
                      <div className="text-center py-8">
                        <div className="w-14 h-14 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                          <IconArticle className="h-7 w-7 text-muted-foreground" />
                        </div>
                        <p className="text-sm font-medium mb-1">No blogs yet</p>
                        <p className="text-xs text-muted-foreground mb-4">
                          Create your first blog post to get started
                        </p>
                        <Button
                          onClick={() => handleOpenCreateDialog(true)}
                          size="sm"
                        >
                          <IconPlus className="h-4 w-4 mr-1.5" />
                          New Blog
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  blogs.map(b => (
                    <TableRow
                      key={b.id}
                      className="cursor-pointer group h-16 transition-colors"
                      onClick={() => navigate(`/blogs/${b.id}`)}
                    >
                      <TableCell>
                        <div className="space-y-0.5">
                          <div className="font-medium group-hover:text-primary transition-colors line-clamp-1">
                            {b.title}
                          </div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {b.slug}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {b.author?.display_name || b.author?.email || (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={b.status} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {b.published_at
                          ? new Date(b.published_at).toLocaleDateString()
                          : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={e => handleDeleteClick(e, b)}
                          >
                            <IconTrash className="h-4 w-4" />
                          </Button>
                          <IconChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={handleOpenCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Blog Post</DialogTitle>
            <DialogDescription>
              Enter a title for your blog post. A slug will be auto-generated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {createError && (
              <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-md border border-destructive/20">
                {createError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="blog-title">Title</Label>
              <Input
                id="blog-title"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                placeholder="My Awesome Blog Post"
                autoFocus
              />
            </div>
            {newTitle && (
              <div className="text-xs text-muted-foreground">
                <span className="font-medium">Slug:</span>{' '}
                <code className="bg-muted px-1 py-0.5 rounded">
                  {generateSlug(newTitle) || '—'}
                </code>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => handleOpenCreateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateBlog}
              disabled={creating || !newTitle.trim()}
            >
              {creating ? 'Creating...' : 'Create Blog'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete blog post?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{' '}
              <span className="font-medium text-foreground">
                {deletingBlog?.title}
              </span>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
